import React from 'react'
import { createChart, type IChartApi, type LineData } from 'lightweight-charts'
import { Card, CardContent, CardHeader, Select } from '@/shared/ui'
import type { ChartPoint, RewardsMasterMetric, RewardsMasterPeriod } from '../../utils/rewardsMasterChart'

type PeriodOption = { value: RewardsMasterPeriod; label: string }

export default function RewardsMasterPerformanceChartCard({
  metric,
  metricLabel,
  period,
  periodOptions,
  onPeriodChange,
  points,
  totalLabel,
  loading,
  error,
  note,
}: {
  metric: RewardsMasterMetric
  metricLabel: string
  period: RewardsMasterPeriod
  periodOptions: PeriodOption[]
  onPeriodChange: (p: RewardsMasterPeriod) => void
  points: ChartPoint[]
  totalLabel: string
  loading: boolean
  error: string | null
  note?: string | null
}) {
  const containerRef = React.useRef<HTMLDivElement | null>(null)
  const chartRef = React.useRef<IChartApi | null>(null)
  const seriesRef = React.useRef<ReturnType<IChartApi['addLineSeries']> | null>(null)

  const hasData = React.useMemo(() => points.some((p) => Number(p.value || 0) > 0), [points])
  const canRenderChart = !loading && hasData
  const [chartReady, setChartReady] = React.useState(false)

  React.useEffect(() => {
    if (!canRenderChart) return
    const el = containerRef.current
    if (!el || chartRef.current) return

    const chart = createChart(el, {
      layout: { background: { color: 'transparent' }, textColor: 'rgba(148,163,184,0.85)' },
      rightPriceScale: { borderColor: 'transparent' },
      timeScale: { borderColor: 'transparent', timeVisible: true },
      grid: { vertLines: { visible: false }, horzLines: { visible: false } },
      crosshair: { mode: 0 },
      handleScroll: { mouseWheel: false, pressedMouseMove: false, horzTouchDrag: false, vertTouchDrag: false } as any,
      handleScale: { mouseWheel: false, pinch: false } as any,
      autoSize: true,
    })
    const series = chart.addLineSeries({ color: lineColor(metric), lineWidth: 2, priceLineVisible: false })

    chartRef.current = chart
    seriesRef.current = series
    setChartReady(true)

    const ro = new ResizeObserver(() => {
      try {
        chart.applyOptions({ autoSize: true })
      } catch {}
    })
    ro.observe(el)

    return () => {
      try {
        ro.disconnect()
      } catch {}
      try {
        chart.remove()
      } catch {}
      chartRef.current = null
      seriesRef.current = null
      setChartReady(false)
    }
  }, [metric, canRenderChart])

  React.useEffect(() => {
    if (!seriesRef.current || !chartReady) return
    seriesRef.current.applyOptions({ color: lineColor(metric) })
    seriesRef.current.setData(points as LineData[])
    try {
      chartRef.current?.timeScale().fitContent()
    } catch {}
  }, [points, metric, chartReady])

  return (
    <Card className="border-0 shadow">
      <CardHeader className="grid gap-3 text-base font-semibold md:grid-cols-[1fr_auto] md:items-start">
        <div>
          <div>Performance - {metricLabel}</div>
          <div className="mt-1 text-xs font-normal text-foreground/60">Total no periodo: {totalLabel}</div>
        </div>
        <div className="md:justify-self-end">
          <Select
            className="w-full md:w-[200px]"
            value={period}
            onChange={(e) => onPeriodChange((e.target as HTMLSelectElement).value as RewardsMasterPeriod)}
          >
            {periodOptions.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </Select>
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        {error && <div className="rounded-md border border-red-500/30 bg-red-500/10 p-2 text-sm text-foreground">{error}</div>}

        {loading ? (
          <div className="text-sm text-foreground/70">Carregando dados do grafico...</div>
        ) : !hasData ? (
          <div className="text-sm text-foreground/70">Sem dados suficientes para exibir o grafico.</div>
        ) : (
          <div ref={containerRef} className="h-64 w-full overflow-hidden rounded-lg bg-muted/20" />
        )}

        {note && <div className="text-xs text-foreground/60">{note}</div>}
      </CardContent>
    </Card>
  )
}

function lineColor(metric: RewardsMasterMetric): string {
  if (metric === 'links') return '#38bdf8'
  if (metric === 'volume') return '#22c55e'
  if (metric === 'commission') return '#f59e0b'
  return '#a78bfa'
}
